import { useEffect, useRef, useState } from "react";

import type { GameType, LocationType } from "../../types";
import Card from "./card";

interface DesktopCardsProps {
  currentLocation: LocationType | null;
  games: GameType[];
  onHover(locationId: string | null): void;
  onLocation(location: LocationType | null): void;
}

export default function DesktopCards({
  currentLocation,
  games,
  onHover,
  onLocation,
}: DesktopCardsProps) {
  const lastLocation = useRef<LocationType | null>(null);
  const [activeGame, setActiveGame] = useState("");

  const handleMouseEnter = (game: GameType) => () => {
    lastLocation.current = game.location;
    onLocation(game.location);
    onHover(game.location.id);

    if (activeGame !== game.id) {
      setActiveGame(game.id);
    }
  };

  const handleMouseLeave = () => {
    onHover(null);
  };

  const scrollToLocation = (location: LocationType) => {
    const firstGame = document.querySelector(
      `[data-location-id="${location.id}"]`
    );

    if (!firstGame) {
      return;
    }

    setActiveGame((firstGame as HTMLElement).dataset.gameId || "");
    firstGame.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollToLocation always the same
  useEffect(() => {
    if (
      currentLocation &&
      (!lastLocation.current || lastLocation.current.id !== currentLocation.id)
    ) {
      lastLocation.current = currentLocation;
      scrollToLocation(currentLocation);
    }
  }, [currentLocation]);

  return (
    <div className="hidden w-1/2 overflow-y-auto pl-20 md:block">
      <div className="flex flex-col items-stretch">
        {games.map((game) => (
          <Card
            data-game-id={game.id}
            data-location-id={game.location.id}
            game={game}
            isActive={game.id === activeGame}
            key={game.id}
            onMouseEnter={handleMouseEnter(game)}
            onMouseLeave={handleMouseLeave}
          />
        ))}
      </div>
    </div>
  );
}
